/**
 * webPush — service-worker registration, Notification permission, and device
 * registration. Mirrors RN's `apps/WunderGuide/utils/notifications` →
 * `billingApi.registerDevice` flow.
 *
 * Subscription is skipped (gracefully) when no VAPID public key is configured
 * — so the foundation works without breaking before the backend ships keys.
 */
import billingApi from '../api/billingApi';
import logger from '../utils/logger';
import packageJson from '../../package.json';
import config from '../../config/env.json';

const VAPID_PUBLIC_KEY: string | undefined =
  (config as any).VAPID_PUBLIC_KEY ||
  (typeof window !== 'undefined' && (window as any).__WG_VAPID_PUBLIC_KEY__) ||
  undefined;

export interface WebPushSetupResult {
  permission: NotificationPermission | 'unsupported';
  registration?: ServiceWorkerRegistration;
  subscription?: PushSubscription;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });
    logger.info('[WebPush] SW registered', { scope: reg.scope });
    return reg;
  } catch (err) {
    logger.warn('[WebPush] SW register failed', { err });
    return null;
  }
}

export async function ensurePermission(): Promise<
  NotificationPermission | 'unsupported'
> {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (
    Notification.permission === 'granted' ||
    Notification.permission === 'denied'
  ) {
    return Notification.permission;
  }
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export async function setupWebPush(): Promise<WebPushSetupResult> {
  const permission = await ensurePermission();
  if (permission !== 'granted') {
    return { permission };
  }

  const registration = await registerServiceWorker();
  if (!registration) return { permission };

  if (!VAPID_PUBLIC_KEY) {
    logger.info(
      '[WebPush] No VAPID public key — SW registered but not subscribed.',
    );
    return { permission, registration };
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          VAPID_PUBLIC_KEY,
        ) as BufferSource,
      });
    } catch (err) {
      logger.warn('[WebPush] PushManager.subscribe failed', { err });
      return { permission, registration };
    }
  }

  try {
    await billingApi.registerDevice({
      device_token: JSON.stringify(subscription.toJSON()),
      platform: 'web',
      device_name: navigator.userAgent.slice(0, 80),
      device_model: navigator.platform || 'web',
      os_version: navigator.userAgent || 'web',
      app_version: packageJson.version,
      notifications_enabled: true,
    });
    logger.info('[WebPush] Device registered for push.');
  } catch (err) {
    logger.warn('[WebPush] registerDevice failed', { err });
  }

  return { permission, registration, subscription };
}
